/* Self-contained clinical PDF of a Digital Health File.
   Generated entirely on-device (jsPDF, no screenshot lib) so the patient owns
   a portable copy that opens with zero internet — lands in Android Downloads.
   Accepts the same mapped shape the on-screen viewer uses. */
import { jsPDF } from 'jspdf';

const NAVY = '#16335b';
const SLATE = '#475569';

export function downloadDhfPdf(d, fileId) {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const W = 210;
  const M = 16;
  const maxW = W - M * 2;
  let y = M;

  const line = (txt, { size = 10, color = '#0f172a', bold = false, gap = 5, indent = 0 } = {}) => {
    pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    pdf.setFontSize(size);
    pdf.setTextColor(color);
    const wrapped = pdf.splitTextToSize(String(txt ?? ''), maxW - indent);
    wrapped.forEach((w) => {
      if (y > 280) { pdf.addPage(); y = M; }
      pdf.text(w, M + indent, y);
      y += gap;
    });
  };

  const heading = (txt) => {
    y += 2;
    if (y > 270) { pdf.addPage(); y = M; }
    pdf.setDrawColor(214, 211, 199);
    pdf.line(M, y - 3, W - M, y - 3);
    line(txt, { size: 11, bold: true, color: NAVY, gap: 6 });
  };

  // Header band
  pdf.setFillColor(231, 221, 201);
  pdf.rect(0, 0, W, 26, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(15);
  pdf.setTextColor(NAVY);
  pdf.text('DocSite — Digital Health File', M, 13);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  pdf.setTextColor(SLATE);
  pdf.text('Patient-owned clinical summary · Created by your doctor during this visit', M, 19);
  y = 36;

  // Identity
  line(d.patient?.name || 'Patient', { size: 13, bold: true, gap: 5 });
  const meta = [
    d.patient?.age && `Age ${d.patient.age}`,
    d.patient?.gender,
    d.visit_date && `Visit: ${d.visit_date}`,
    d.consultation_type,
    d.clinic_location,
  ].filter(Boolean).join('  ·  ');
  line(meta, { size: 9, color: SLATE, gap: 5 });
  const docLine = [d.doctor?.name, d.doctor?.specialty].filter(Boolean).join(' · ');
  if (docLine) line(docLine, { size: 9, color: SLATE, gap: 6 });

  if (d.reason_for_visit) { heading('Reason for Visit'); line(d.reason_for_visit, { size: 10, gap: 5 }); }

  if ((d.assessment || []).length) {
    heading("Doctor's Assessment");
    d.assessment.forEach((a) => line(`•  ${a}`, { size: 10, gap: 5, indent: 2 }));
  }

  if ((d.medicines || []).length) {
    heading('Medicines Prescribed');
    d.medicines.forEach((m) => {
      const head = [m.name, m.strength].filter(Boolean).join(' ');
      const det = [m.dose, m.frequency, m.duration].filter(Boolean).join(', ');
      line(head, { size: 10, bold: true, gap: 4.5, indent: 2 });
      line([det, m.notes].filter(Boolean).join('  —  '), { size: 9, color: SLATE, gap: 5, indent: 6 });
    });
    line('Take medicines after food. Drink plenty of fluids and rest well.', { size: 8.5, color: SLATE, gap: 5 });
  }

  if ((d.tests || []).length) {
    heading('Tests Advised');
    d.tests.forEach((t) => line(`•  ${t}`, { size: 10, gap: 5, indent: 2 }));
  }

  if ((d.red_flags || []).length) {
    heading('Red Flags — Seek Care If');
    d.red_flags.forEach((r) => line(`!  ${r}`, { size: 10, color: '#b91c1c', gap: 5, indent: 2 }));
    line('Seek immediate medical attention if any of the above occur.', { size: 9, bold: true, color: '#b91c1c', gap: 5 });
  }

  if (d.follow_up) { heading('Follow-up Plan'); line(d.follow_up, { size: 10, gap: 5 }); }

  // Provenance footer
  heading('Record Provenance');
  if (d.doctor?.name) line(`Creator: ${d.doctor.name}`, { size: 9, gap: 4.5 });
  if (d.doctor?.qualifications) line(d.doctor.qualifications, { size: 8.5, color: SLATE, gap: 4.5 });
  if (d.doctor?.registration) line(`Reg. No. ${d.doctor.registration}`, { size: 8.5, color: SLATE, gap: 4.5 });
  line(`File ID: ${fileId || d.file_id || '—'}`, { size: 8.5, color: SLATE, gap: 4.5 });
  if (d.created_at_str) line(`Created: ${d.created_at_str}`, { size: 8.5, color: SLATE, gap: 4.5 });
  line('Locked record — non-editable after creation. Backed up on DocSite.', { size: 8, color: SLATE, gap: 5 });

  const safeId = (fileId || d.file_id || 'health-file').replace(/[^a-zA-Z0-9-_]/g, '');
  pdf.save(`DocSite-HealthFile-${safeId}.pdf`);
}
